"use client";

import { CalendarDotsIcon } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";

import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks";
import { useHasPermission } from "@/hooks/usePermissions";
import { useWorkflow } from "@/queries/useWorkflow";
import { useHomePmViewStore } from "@/store/useHomePmViewStore";

import { useDeadlineAvi } from "../queries/useDeadlineAvi";
import { useProjectDeadline } from "../queries/useProjectDeadline";
import { useProjectStatus } from "../queries/useProjectStatus";
import { useRecentAvi } from "../queries/useRecentAvi";
import { useRecentProject } from "../queries/useRecentProject";
import { DeadlineCard } from "./deadlineCard";
import { HomePanelSection } from "./homePanelSection";
import { RecentAviTable } from "./recentAviTable";
import { RecentProjectsTable } from "./recentProjectsTable";
import { StatusProjectCard } from "./statusProjectCard";
import { WorkflowAviCard } from "./workflowAviCard";

export function HomeProjectsPanel() {
  const t = useTranslations("home");
  const user = useUser();
  const params = { account_id: user.account_id, role: user.role };

  const canViewAvi = useHasPermission("panel:avi");
  const view = useHomePmViewStore((s) => s.view);
  const setView = useHomePmViewStore((s) => s.setView);

  const {
    data: recentProjectData,
    isError: recentProjectError,
    isLoading: recentProjectLoading,
  } = useRecentProject(params);
  const {
    data: deadlineProjectData,
    isError: deadlineProjectError,
    isLoading: deadlineProjectLoading,
  } = useProjectDeadline(params);
  const {
    data: statusProjectData,
    isError: statusProjectError,
    isLoading: statusProjectLoading,
  } = useProjectStatus(params);

  const {
    data: recentAvisData,
    isError: recentAvisError,
    isLoading: recentAvisLoading,
  } = useRecentAvi(params, canViewAvi);
  const {
    data: deadlineAviData,
    isError: deadlineAviError,
    isLoading: deadlineAviLoading,
  } = useDeadlineAvi(params, canViewAvi);
  const {
    data: workflowData,
    isError: workflowError,
    isLoading: workflowLoading,
  } = useWorkflow(params, canViewAvi);

  return (
    <Box className="flex min-h-0 min-w-0 flex-[4_4_0] flex-col self-stretch rounded-4xl bg-white">
      <div className="mx-1 mt-6 mb-6 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex w-full shrink-0 items-center justify-between border-b border-neutral-100 px-6 pt-0 pb-3 xl:pb-4.5">
          <h1 className="font-heading text-2xl leading-7 font-medium text-neutral-950">
            {t("greeting", { name: user.name })}
          </h1>
          {canViewAvi && (
            <div className="flex h-7 items-center gap-2">
              <div className="flex gap-0.5 rounded-[10px] bg-[#E7E7E7] p-0.5">
                <Button
                  variant="ghost"
                  size="xs"
                  className={
                    view === "projects"
                      ? "h-6 w-17.5 rounded-lg bg-white shadow-xs hover:bg-white"
                      : "h-6 w-17.5 rounded-lg"
                  }
                  aria-pressed={view === "projects"}
                  onClick={() => setView("projects")}
                >
                  {t("view.projects")}
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  className={
                    view === "avis"
                      ? "h-6 w-17.5 rounded-lg bg-white shadow-xs hover:bg-white"
                      : "h-6 w-17.5 rounded-lg"
                  }
                  aria-pressed={view === "avis"}
                  onClick={() => setView("avis")}
                >
                  {t("view.avis")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col justify-between px-6 pt-4">
          {!canViewAvi || view === "projects" ? (
            <>
              <HomePanelSection
                isLoading={recentProjectLoading}
                isError={recentProjectError}
                loadingText={t("panel.recentProject.loading")}
                errorText={t("panel.recentProject.error")}
                showDivider
              >
                <RecentProjectsTable items={recentProjectData} />
              </HomePanelSection>

              <HomePanelSection
                isLoading={deadlineProjectLoading}
                isError={deadlineProjectError}
                loadingText={t("panel.projectDeadline.loading")}
                errorText={t("panel.projectDeadline.error")}
                showDivider
              >
                <DeadlineCard data={deadlineProjectData} icon={<CalendarDotsIcon size={24} />} />
              </HomePanelSection>

              <HomePanelSection
                isLoading={statusProjectLoading}
                isError={statusProjectError}
                loadingText={t("panel.projectStatus.loading")}
                errorText={t("panel.projectStatus.error")}
              >
                <StatusProjectCard data={statusProjectData} />
              </HomePanelSection>
            </>
          ) : (
            <>
              <HomePanelSection
                isLoading={recentAvisLoading}
                isError={recentAvisError}
                loadingText={t("panel.recentAvi.loading")}
                errorText={t("panel.recentAvi.error")}
                showDivider
              >
                <RecentAviTable items={recentAvisData} />
              </HomePanelSection>

              <HomePanelSection
                isLoading={deadlineAviLoading}
                isError={deadlineAviError}
                loadingText={t("panel.deadline.loading")}
                errorText={t("panel.deadline.error")}
                showDivider
              >
                <DeadlineCard data={deadlineAviData} icon={<CalendarDotsIcon size={24} />} />
              </HomePanelSection>

              <HomePanelSection
                isLoading={workflowLoading}
                isError={workflowError}
                loadingText={t("panel.workflow.loading")}
                errorText={t("panel.workflow.error")}
              >
                <WorkflowAviCard data={workflowData} />
              </HomePanelSection>
            </>
          )}
        </div>
      </div>
    </Box>
  );
}
